import { useEffect, useState } from 'react';

/**
 * Aiguillage shell mobile / shell bureau (C11, spec § 1.1).
 * Source : media query « largeur < 768 px », combinée à un réglage persistant
 * à trois valeurs. Les tablettes (≥ 768 px) gardent le bureau au palier 1.
 */
export type ShellMode = 'mobile' | 'desktop';
export type ShellPreference = 'auto' | 'mobile' | 'desktop';

export const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
export const SHELL_PREFERENCE_KEY = 'anagogia.shellMode';

const PREFERENCES: ShellPreference[] = ['auto', 'mobile', 'desktop'];

export function readShellPreference(): ShellPreference {
  try {
    const raw = window.localStorage.getItem(SHELL_PREFERENCE_KEY);
    return PREFERENCES.includes(raw as ShellPreference) ? (raw as ShellPreference) : 'auto';
  } catch {
    return 'auto';
  }
}

export function writeShellPreference(preference: ShellPreference): void {
  try {
    if (preference === 'auto') window.localStorage.removeItem(SHELL_PREFERENCE_KEY);
    else window.localStorage.setItem(SHELL_PREFERENCE_KEY, preference);
  } catch {
    /* stockage indisponible : le forçage ne survit pas au rechargement, l'aiguillage reste automatique */
  }
}

function matchesMobile(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
    : false;
}

export function resolveShellMode(preference: ShellPreference, isNarrow: boolean): ShellMode {
  if (preference === 'mobile' || preference === 'desktop') return preference;
  return isNarrow ? 'mobile' : 'desktop';
}

export function useShellMode(): ShellMode {
  const [isNarrow, setIsNarrow] = useState<boolean>(matchesMobile);
  const [preference] = useState<ShellPreference>(readShellPreference);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = (e: { matches: boolean }) => setIsNarrow(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return resolveShellMode(preference, isNarrow);
}
